/**
 * Links to one event on a stock's Events page: `/s/<exchange>/<ticker>/events/<eventKey>`.
 *
 * The event list's `id` cannot name an event: it is a per-table primary key, and the
 * unified feed mixes event models whose keys collide. The backend sends `eventKey`
 * instead (the CIQ keydevid, or `cal<pk>` for curated index-calendar rows), plus
 * `aliasKeys` on merged earnings rows, so a link made from any event folded into the
 * row still resolves (ortex/ted/ted#2628).
 */
import type { EventListRow } from "@/models/event-page";
import { stockPath } from "./stock-url";

/** A key the backend can have issued: a keydevid, or `cal<pk>`. Anything else is not looked up. */
const EVENT_KEY_PATTERN = /^(?:\d{1,20}|cal\d{1,20})$/;

export const isEventKey = (value: unknown): value is string => typeof value === "string" && EVENT_KEY_PATTERN.test(value);

/**
 * The linkable key of a row, or null when it has none. Falls back to the keydevid
 * (`externalId`), which every Key Developments row has carried since #1633.
 */
export const eventKey = (row: EventListRow): string | null => {
    if (isEventKey(row.eventKey)) return row.eventKey;
    return row.externalId != null ? String(row.externalId) : null;
};

/** A key unique within one company's list, for selection and React keys; linkable rows use their event key. */
export const eventSelectionKey = (row: EventListRow): string => eventKey(row) ?? `row-${row.type ?? ""}-${row.id}`;

/** The row a link names, by its own key first and then by a key merged into it; undefined when it is gone. */
export const findLinkedEvent = (rows: EventListRow[], key: string): EventListRow | undefined =>
    rows.find((row) => eventKey(row) === key) ?? rows.find((row) => Array.isArray(row.aliasKeys) && row.aliasKeys.includes(key));

/** `/s/<exchange>/<ticker>/events[/<key>]`. */
export const stockEventPath = (stock: { exchangesymbol: string; ticker: string }, key: string | null): string => stockPath(stock, key ? `events/${encodeURIComponent(key)}` : "events");
